import { UnifiedTool, Conversation } from "@/types"
import type { PreviousConversationsMeta } from "@/types/eval"
import type { UploadingFile } from "@/components/Conversation/FileUploadTypes"
import type { EvaluationCase, EvaluationResult, Evaluator } from "@/types"
import type { HttpRequestAuthConfig } from "@/types/httpRequestTool"

export function safe(value: unknown, maxLength?: number): string {
  const sanitized = String(value ?? "")
    .replace(/[|\n]/g, " ")
    .trim()
  if (typeof maxLength === "number") {
    return sanitized.length > maxLength
      ? sanitized.slice(0, maxLength)
      : sanitized
  }
  return sanitized
}

/**
 * Build a compact, human-readable snapshot summary for unified tools.
 * This is consumed by the page snapshot builder via a hidden element
 * with the `data-copilot-value` attribute inside the field container.
 */
export function summarizeUnifiedTools(
  tools: UnifiedTool[] | undefined
): string {
  if (!tools || tools.length === 0) return ""

  return tools
    .map((t) => {
      if (t.type === "knowledge_base") {
        const when = t.invocationInstruction
          ? `; when=${safe(t.invocationInstruction)}`
          : ""
        const name = t.name ? `name=${safe(t.name)}` : "name=knowledge_base"
        return `kb:${name}${when}`
      }
      if (t.type === "web_search") {
        const domain = t.domain ? `domain=${safe(t.domain)}` : "domain="
        const when = t.invocationInstruction
          ? `; when=${safe(t.invocationInstruction)}`
          : ""
        return `web:${domain}${when}`
      }
      if (t.type === "call_agent") {
        const ver =
          typeof t.version === "number"
            ? t.version === -1
              ? "@latest"
              : `@v${t.version}`
            : ""
        const when = t.invocationInstruction
          ? `; when=${safe(t.invocationInstruction)}`
          : ""
        return `agent:${safe(t.name)}${ver}${when}`
      }
      if (t.type === "http_request") {
        const method = t.httpRequest?.method
          ? `method=${safe(t.httpRequest.method)}`
          : "method="
        const url = t.httpRequest?.urlTemplate
          ? `url=${safe(t.httpRequest.urlTemplate, 120)}`
          : "url="
        const when = t.invocationInstruction
          ? `; when=${safe(t.invocationInstruction)}`
          : ""
        return `http:${method}; ${url}${when}`
      }
      if (t.type === "text2speech") {
        const model = t.text2speech?.modelId
          ? `model=${safe(t.text2speech.modelId)}`
          : "model="
        const voice = t.text2speech?.voiceId
          ? `voice=${safe(t.text2speech.voiceId)}`
          : "voice="
        const language = t.text2speech?.languageCode
          ? `lang=${safe(t.text2speech.languageCode)}`
          : "lang=auto"
        const when = t.invocationInstruction
          ? `; when=${safe(t.invocationInstruction)}`
          : ""
        return `tts:${model}; ${voice}; ${language}${when}`
      }
      if (t.type === "payment") {
        const when = t.invocationInstruction
          ? `; when=${safe(t.invocationInstruction)}`
          : ""
        return `payment${when}`
      }
      return safe(t.type)
    })
    .join(" | ")
}

function maskSecret(raw: string | undefined | null): string | undefined {
  if (!raw) return raw ?? undefined
  return "***"
}

function maskHttpAuth(auth: HttpRequestAuthConfig | undefined) {
  if (!auth) return null
  return {
    ...auth,
    token: maskSecret(auth.token),
    apiKey: maskSecret(auth.apiKey),
    password: maskSecret(auth.password),
    headerValue: maskSecret(auth.headerValue),
  }
}

function stableJsonStringify(value: unknown): string {
  try {
    return JSON.stringify(value)
  } catch {
    return ""
  }
}

export function summarizeRequestFormToolsFullDetail(
  tools: UnifiedTool[] | undefined
): string {
  const requestFormTools = (tools ?? [])
    .filter((tool) => tool.type === "request_form")
    .map((tool) => ({
      toolId: tool.id,
      type: tool.type,
      name: tool.name || "",
      invocationInstruction: tool.invocationInstruction || "",
      formSchema: tool.customFormSchema || null,
    }))

  return stableJsonStringify({ tools: requestFormTools })
}

export function summarizeMemoryToolsFullDetail(
  tools: UnifiedTool[] | undefined
): string {
  const memoryTools = (tools ?? [])
    .filter((tool) => tool.type === "memory")
    .map((tool) => ({
      toolId: tool.id,
      type: tool.type,
      name: tool.name || "",
      invocationInstruction: tool.invocationInstruction || "",
    }))

  return stableJsonStringify({ tools: memoryTools })
}

export function summarizeHttpRequestToolsFullDetail(
  tools: UnifiedTool[] | undefined
): string {
  const httpTools = (tools ?? [])
    .filter((tool) => tool.type === "http_request")
    .map((tool) => ({
      toolId: tool.id,
      type: tool.type,
      name: tool.name || "",
      invocationInstruction: tool.invocationInstruction || "",
      httpRequest: tool.httpRequest
        ? {
            ...tool.httpRequest,
            auth: maskHttpAuth(tool.httpRequest.auth),
          }
        : null,
    }))

  return stableJsonStringify({ tools: httpTools })
}

/**
 * Build a compact summary for attachments. Emits entries like:
 *   fileName.pdf#abcd1234; scope=P; status=uploaded
 * Joined by " | ".
 */
export function summarizeAttachments(
  files: UploadingFile[] | undefined
): string {
  if (!files || files.length === 0) return ""

  return files
    .map((f) => {
      return `name=${safe(f.file?.name)}; id=${f.uuid}`
    })
    .join(" | ")
}

/**
 * Build a compact summary for a single conversation, suitable for history page.
 * Format:
 *   id=<id>; role=<role>; agentVer=v<ver>; content=<content>
 */
export function summarizeConversation(conv: Conversation | undefined): string {
  if (!conv) return ""
  const parts = [
    typeof conv.id === "number" ? `id=${conv.id}` : "",
    conv.role ? `role=${safe(conv.role)}` : "",
    typeof conv.meta?.agentVersion === "number"
      ? `agentVersion=v${conv.meta.agentVersion}`
      : "",
    conv.meta?.agentProfile?.name
      ? `agentName=${safe(conv.meta.agentProfile.name)}`
      : "",
    conv.content ? `content=${safe(conv.content)}` : "",
  ].filter(Boolean)
  return parts.join("; ")
}

/**
 * Build a compact summary for average score card.
 * Format:
 *   evaluator=<name>; avg=<avg>; min=<min>; max=<max>; completion=<rate>% (<completed>/<total>)
 */
export function summarizeAverageScoreCard(params: {
  evaluatorName?: string
  avgScore?: number | null
  min?: number
  max?: number
  completionRate?: number
  completed?: number
  total?: number
}): string {
  const parts = [
    params.evaluatorName ? `evaluator=${safe(params.evaluatorName)}` : "",
    typeof params.avgScore === "number"
      ? `avg=${params.avgScore.toFixed(2)}`
      : "",
    typeof params.min === "number" ? `min=${params.min.toFixed(2)}` : "",
    typeof params.max === "number" ? `max=${params.max.toFixed(2)}` : "",
    typeof params.completionRate === "number"
      ? `completion=${Math.round(params.completionRate)}%`
      : "",
    typeof params.completed === "number" && typeof params.total === "number"
      ? `progress=${params.completed}/${params.total}`
      : "",
  ].filter(Boolean)
  return parts.join("; ")
}

/**
 * Build a compact summary for previous conversations metadata.
 * Format: count=<n>; source=<historyId>; target=<conversationId>
 */
export function summarizePreviousConversations(
  prev?: PreviousConversationsMeta
): string {
  if (!prev) return ""
  const parts = [
    `count=${prev.previousConversationCount ?? 0}`,
    `source=${prev.sourceHistoryId ?? ""}`,
    `target=${prev.targetConversationId ?? ""}`,
  ]
  return parts.join("; ")
}

/**
 * Build a compact table summary for evaluation cases.
 * Format:
 *   count=<n>; selected=<n>; running=<n>; failed=<n>; withUsage=<n>; avg=<avg>
 */
export function summarizeEvaluationTable(params: {
  cases?: EvaluationCase[]
  selectedIds?: string[]
  selectedEvaluator?: Evaluator | null
  results?: EvaluationResult[]
}): string {
  const total = Array.isArray(params.cases) ? params.cases.length : 0
  const selected = Array.isArray(params.selectedIds)
    ? params.selectedIds.length
    : 0
  const evaluatorId = params.selectedEvaluator?.id
  const filteredResults = Array.isArray(params.results)
    ? params.results.filter((r) =>
        evaluatorId ? r.evaluatorId === evaluatorId : true
      )
    : []
  const running = filteredResults.filter(
    (r) => r.status === "running" || r.status === "pending"
  ).length
  const failed = filteredResults.filter((r) => r.status === "failed").length
  const withUsage = filteredResults.filter(
    (r) =>
      typeof r.costCredits === "number" || typeof r.responseTimeMs === "number"
  ).length
  const scored = filteredResults.filter(
    (r) => typeof r.score === "number" && Number.isFinite(r.score as number)
  )
  const avg =
    scored.length > 0
      ? scored.reduce((acc, r) => acc + ((r.score as number) || 0), 0) /
        scored.length
      : undefined
  const parts = [
    `count=${total}`,
    `selected=${selected}`,
    `running=${running}`,
    `failed=${failed}`,
    `withUsage=${withUsage}`,
    typeof avg === "number" ? `avg=${avg.toFixed(2)}` : "",
  ].filter(Boolean)
  return parts.join("; ")
}
